import { useEffect, useRef, useState } from 'react'

const kShowDelayMs = 500
const kMinDisplayMs = 1000

/**
 * useDelayedVisibility
 *
 * Delays showing a UI element until an activity has been ongoing for `showDelayMs`,
 * then keeps it visible for at least `minDisplayMs` once shown.
 *
 * This prevents jarring flashes for fast operations — if the activity completes
 * before the show delay, the element never appears at all.
 *
 * @param isActive - Whether the activity is currently in progress
 * @param options.showDelayMs - How long to wait before showing (default: kShowDelayMs)
 * @param options.minDisplayMs - Minimum time to stay visible once shown (default: kMinDisplayMs)
 * @returns Whether the element should be visible
 */
export function useDelayedVisibility(isActive: boolean, options: { showDelayMs?: number; minDisplayMs?: number } = {}): boolean {
  const { showDelayMs = kShowDelayMs, minDisplayMs = kMinDisplayMs } = options

  const [visible, setVisible] = useState(false)
  const visibleSinceRef = useRef<number | null>(null)

  useEffect(() => {
    if (!isActive) {
      // Activity finished — hold visible until min display time has passed
      if (!visibleSinceRef.current) {
        setVisible(false)
        return
      }

      const elapsed = Date.now() - visibleSinceRef.current
      const remaining = minDisplayMs - elapsed

      if (remaining <= 0) {
        setVisible(false)
        visibleSinceRef.current = null
        return
      }

      const timer = setTimeout(() => {
        setVisible(false)
        visibleSinceRef.current = null
      }, remaining)
      return () => clearTimeout(timer)
    }

    // Activity started — delay before showing
    const timer = setTimeout(() => {
      setVisible(true)
      visibleSinceRef.current = Date.now()
    }, showDelayMs)
    return () => clearTimeout(timer)
  }, [isActive, showDelayMs, minDisplayMs])

  return visible
}
